import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { vortxColors, retroColors, vortxTypography } from '@vortx/shared-ui';
import {
    CheckCircle,
    RadioButtonUnchecked,
    LocationOn,
    ElectricBolt,
    Assessment,
    LocalGasStation,
} from '@mui/icons-material';

// Map field keys to icons and tab indices
const FIELD_CONFIG = {
    zipCode: { icon: LocationOn, tabIndex: 0, label: 'Zip Code' },
    location: { icon: LocationOn, tabIndex: 0, label: 'Location' },
    loadProfile: { icon: Assessment, tabIndex: 2, label: 'Load Profile' },
    tariff: { icon: ElectricBolt, tabIndex: 1, label: 'Tariff' },
    voltageLevel: { icon: ElectricBolt, tabIndex: 1, label: 'Voltage Level' },
    generatorFuelType: { icon: LocalGasStation, tabIndex: 0, label: 'Generator Fuel' },
};

const RetroInputChecklist = ({ requiredFields, missingFields, onNavigateToTab, accentColor = '#b28c50' }) => {
    // Convert requiredFields object to array for rendering
    const fieldEntries = Object.entries(requiredFields || {});

    if (fieldEntries.length === 0) {
        return null;
    }

    // Create a set of missing field keys for quick lookup
    const missingSet = new Set((missingFields || []).map(f => f.key));

    // Count completed vs total
    const completedCount = fieldEntries.filter(([key]) => !missingSet.has(key)).length;
    const totalCount = fieldEntries.length;
    const allComplete = completedCount === totalCount;

    const handleFieldClick = (key) => {
        const config = FIELD_CONFIG[key];
        if (config && onNavigateToTab) {
            onNavigateToTab(config.tabIndex);
        }
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1.5,
                pb: 1,
                borderBottom: `1px solid ${accentColor}40`,
            }}>
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.15em',
                    color: accentColor,
                    transition: 'color 0.3s ease',
                }}>
                    Input Checklist
                </Typography>
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.65rem',
                    color: allComplete ? vortxColors.success : retroColors.muted,
                    fontWeight: allComplete ? 600 : 400,
                }}>
                    {completedCount}/{totalCount}
                </Typography>
            </Box>

            {fieldEntries.map(([key, field]) => {
                const isComplete = !missingSet.has(key);
                const config = FIELD_CONFIG[key] || {};
                const Icon = config.icon || Assessment;

                return (
                    <Box
                        key={key}
                        onClick={() => !isComplete && handleFieldClick(key)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            padding: '0.4rem 0.5rem',
                            cursor: isComplete ? 'default' : 'pointer',
                            borderLeft: '2px solid',
                            borderLeftColor: isComplete ? alpha(vortxColors.success, 0.38) : `${accentColor}40`,
                            background: isComplete ? alpha(vortxColors.success, 0.05) : 'transparent',
                            transition: 'all 0.15s ease',
                            opacity: isComplete ? 0.7 : 1,
                            '&:hover': !isComplete ? {
                                borderLeftColor: accentColor,
                                background: `${accentColor}15`,
                            } : {},
                        }}
                    >
                        {isComplete ? (
                            <CheckCircle sx={{ fontSize: 14, color: vortxColors.success }} />
                        ) : (
                            <RadioButtonUnchecked sx={{ fontSize: 14, color: accentColor }} />
                        )}
                        <Icon sx={{
                            fontSize: 12,
                            color: isComplete ? alpha(vortxColors.success, 0.5) : retroColors.muted,
                        }} />
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            color: isComplete ? retroColors.muted : vortxColors.gray,
                            textDecoration: isComplete ? 'line-through' : 'none',
                            flex: 1,
                        }}>
                            {field.label || config.label || key}
                        </Typography>
                    </Box>
                );
            })}

            {allComplete && (
                <Box sx={{
                    mt: 1.5,
                    padding: '0.5rem',
                    background: alpha(vortxColors.success, 0.1),
                    border: `1px solid ${alpha(vortxColors.success, 0.3)}`,
                    textAlign: 'center',
                }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.65rem',
                        color: vortxColors.success,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                    }}>
                        Ready to Optimize
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default RetroInputChecklist;
